import useWebSocket, { ReadyState } from "react-use-websocket";
import { useCallback, useEffect, useMemo, useState } from "react";
import { annotationMarkdown } from "fe-web-components/references/utils";
import { CitationData, References } from "fe-web-components/references/types";

type WebSocketJsonMessage = {
  start?: boolean;
  end?: boolean;
  chunk?: string;
  footnote?: StreamFootnote;
  citation?: CitationData;
  index?: number;
  metadata?: Record<string, any>;
  questionId?: string;
  sessionid?: string;
};

export type StreamFootnote = {
  id: string;
  citationid: string;
  text: string;
};

export type Stream = {
  text?: string;
  chunks?: string[];
  metadata?: Record<string, any>;
  references?: References;
  close?: () => void;
};

export type StreamManager = {
  connectionId: string;
  streaming: boolean;
  getStreamById: (sessionId: string, questionId: string) => Stream;
  closeStream: (streamId: string) => void;
};

const yes = () => true;

const useStreamManager = (
  endpoint: string,
  withDebug: boolean,
): StreamManager => {
  const [streamState, setStreamState] = useState<{
    streams: Record<string, Stream>;
    streaming: boolean;
  }>({ streams: {}, streaming: false });
  const [connectionId, setConnectionId] = useState("");

  const getStreamById = useCallback(
    (sessionId: string, questionId: string): Stream => {
      if (!streamState.streaming) return null;
      const streamId = `${sessionId}-${questionId}`;
      return streamState.streams[streamId] || null;
    },
    [streamState],
  );

  const closeStream = useCallback(
    (streamId: string) => {
      setStreamState((prevState) => {
        if (withDebug) console.log("closing", streamId);
        const newStreams = { ...prevState.streams };
        delete newStreams[streamId];
        return {
          ...prevState,
          streams: newStreams,
          streaming: Object.keys(newStreams).length > 0,
        };
      });
    },
    [withDebug],
  );

  const consumeConnectionMsg = useCallback(
    (event: MessageEvent) => {
      // grab connectionId passed from server
      try {
        const obj = JSON.parse(event.data);
        if (obj?.connectionId && obj.connectionId !== connectionId) {
          console.log("WS Received ConnectionID", obj);
          //update connectionId in session store
          setConnectionId(obj.connectionId);
        }
      } catch (e) {}
    },
    [connectionId],
  );

  const updateStream = useCallback(
    (message: WebSocketJsonMessage) => {
      const {
        start,
        end,
        chunk,
        index,
        metadata,
        sessionid,
        questionId,
        footnote,
        citation,
      } = message as WebSocketJsonMessage;
      const streamId = `${sessionid}-${questionId}`;
      setStreamState((prevState) => {
        // new stream started
        if (start) {
          return {
            ...prevState,
            streams: {
              ...prevState.streams,
              [streamId]: { text: "", close: () => closeStream(streamId) },
            },
            streaming: true,
          };
        }
        // stream ended
        if (end) {
          const newStreams = { ...prevState.streams };
          delete newStreams[streamId];
          return {
            ...prevState,
            streams: newStreams,
            streaming: Object.keys(newStreams).length > 0,
          };
        }

        // if no questionId, ignore message
        if (!questionId) return prevState;

        const currentStream = prevState.streams[streamId];
        // if no stream found, ignore message
        if (!currentStream) return prevState;

        let {
          text = "",
          references = {},
          chunks = [],
          metadata: prevMetadata = {},
          close,
        } = currentStream;
        const { citations = [], footnotes = [] } = references;

        if (chunk || chunk === "") {
          chunks[index] = chunk;
          const missingChunk = chunks.findIndex((c) => c === undefined);
          text = chunks
            .slice(0, missingChunk > -1 ? missingChunk : undefined)
            .join("");
        }

        if (footnote) {
          text += annotationMarkdown(
            footnote.citationid,
            footnote.id,
            footnote.text,
          );
          footnotes.push(footnote);
        }

        if (citation) citations.push(citation);

        return {
          ...prevState,
          streams: {
            ...prevState.streams,
            [streamId]: {
              text,
              chunks,
              references: { citations, footnotes },
              metadata: { ...prevMetadata, ...metadata },
              close,
            },
          },
        };
      });
    },
    [closeStream],
  );

  const { lastJsonMessage, sendMessage, readyState } = useWebSocket(endpoint, {
    share: true,
    onMessage: consumeConnectionMsg,
    shouldReconnect: yes,
    heartbeat: true,
  });

  // on first connection, get connection id
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      // ask for connection id (actual msg doesn't matter)
      // the server will send back the connectionid, which will be handled in the onMessage callback
      if (withDebug) console.log("WS Asked for ConnectionID");
      sendMessage("get connectionid");
    }
  }, [readyState, sendMessage, withDebug]);

  // update stream on message
  useEffect(() => {
    if (!lastJsonMessage) return;
    //if (withDebug) console.log("WebSocket message received:", lastJsonMessage);
    updateStream(lastJsonMessage);
  }, [lastJsonMessage, updateStream, withDebug]);

  return useMemo(() => {
    return {
      streaming: streamState.streaming,
      getStreamById,
      closeStream,
      connectionId,
    };
  }, [streamState.streaming, getStreamById, closeStream, connectionId]);
};

export default useStreamManager;
